import axios, { AxiosResponse } from 'axios'
import { criarRequestsDocumentos } from 'app/modules/apps/shared/documentos-pessoa/_requests'

import {
  CargoEnum,
  FuncionarioDtoAdd,
  FuncionarioDtoCreate,
  FuncionarioDtoDadosAdicionais,
  FuncionarioDtoUpdate,
  FuncionarioMemed,
  FuncionariosQueryResponse,
  FuncionarioView,
} from './_models'
import { CboView, LotacaoDtoCreate, LotacaoDtoUpdate, LotacaoView } from './_lotacaoModels'
import { ID } from '../../../../../../_metronic/helpers'
import {
  EnderecoDtoCreate,
  EnderecoDtoUpdate,
  EnderecoList,
  EnderecoView,
} from '../../../empresa/empresas-list/core/_models'
import { FuncionarioDtoEspecialidades } from 'app/modules/apps/admin/profissionais-saude/profissional-saude/core/_models'
import { UsuarioView } from 'app/modules/apps/admin/usuarios/usuarios-list/core/_models'

const API_URL = process.env.REACT_APP_API_URL
const FUNCIONARIO_URL = `${API_URL}/funcionarios`
const FUNCIONARIO_LOGADO_URL = `${FUNCIONARIO_URL}/me`
const FUNCIONARIO_MEMED_URL = `${FUNCIONARIO_URL}/memed`
const CARGOS_URL = `${API_URL}/cargos`
const FUNCIONARIO_EMPRESAS_URL = `${API_URL}/funcionarios/empresa/`

export const getFuncionarios = (query: string): Promise<FuncionariosQueryResponse> => {
  return axios
    .get(`${FUNCIONARIO_URL}?${decodeURIComponent(query)}`)
    .then((d: AxiosResponse<FuncionariosQueryResponse>) => d.data)
}

/**
 * Busca todos os funcionários de empresas com perfil responsável
 * @param query string
 * @returns FuncionarioList
 */
export const getFuncionariosAdmin = (query: string): Promise<FuncionariosQueryResponse> => {
  return axios
    .get(`${FUNCIONARIO_URL}/admin?${query}`)
    .then((d: AxiosResponse<FuncionariosQueryResponse>) => d.data)
}

export function getCargos() {
  return axios
    .get<CargoEnum[]>(CARGOS_URL)
    .then((response: AxiosResponse<CargoEnum[]>) => response.data)
}

export const getFuncionariosEmpresa = (
  id: string,
  query: string
): Promise<FuncionariosQueryResponse> => {
  return axios
    .get(`${FUNCIONARIO_EMPRESAS_URL}${id}?${query}`)
    .then((d: AxiosResponse<FuncionariosQueryResponse>) => d.data)
}

export const getFuncionarioById = (id: ID): Promise<FuncionarioView> => {
  return axios
    .get(`${FUNCIONARIO_URL}/${id}`)
    .then((response: AxiosResponse<FuncionarioView>) => response.data)
}
export const getFuncionarioByCPFEmpresa = (
  cpf: string,
  idEmpresa: ID
): Promise<FuncionarioView> => {
  return axios
    .get(`${FUNCIONARIO_URL}/empresa/${idEmpresa}/cpf/${cpf}`)
    .then((response: AxiosResponse<FuncionarioView>) => response.data)
}

export const getMemedFuncionario = (
  atendimentoId: number
): Promise<FuncionarioMemed | undefined> => {
  return axios
    .get(`${FUNCIONARIO_MEMED_URL}?idAtendimento=${atendimentoId}`)
    .then((response: AxiosResponse<FuncionarioMemed>) => response.data)
}
export const getFuncionarioPessoaEmpresa = (
  idEmpresa: ID,
  idPessoa: ID
): Promise<FuncionarioView[]> => {
  return axios
    .get(`${FUNCIONARIO_EMPRESAS_URL}${idEmpresa}/pessoa/${idPessoa}`)
    .then((response: AxiosResponse<FuncionarioView[]>) => response.data)
}

export const getFuncionarioLogado = (): Promise<FuncionarioView> => {
  return axios.get(`${FUNCIONARIO_LOGADO_URL}`).then((response) => response.data)
}

/**
 * Cria um funcionário na empresa.
 * @param idEmpresa Identificador da empresa
 * @param funcionario Objeto FuncionarioDtoCreate
 * @returns FuncionarioView
 */
export const createFuncionario = (
  idEmpresa: number,
  funcionario: FuncionarioDtoCreate
): Promise<FuncionarioView> => {
  return axios
    .post(`${FUNCIONARIO_EMPRESAS_URL}${idEmpresa}`, funcionario)
    .then((response: AxiosResponse<FuncionarioView>) => response.data)
}

/**
 * Vincula funcionário já existente em empresa.
 * @param idEmpresa Identificador da empresa
 * @param funcionario Objeto FuncionarioDtoAdd
 * @returns FuncionarioView
 */
export function vincularFuncionarioEmpresa(idEmpresa: number, funcionario: FuncionarioDtoAdd) {
  return axios
    .put(`${FUNCIONARIO_URL}/empresa/${idEmpresa}`, funcionario)
    .then((resp: AxiosResponse<FuncionarioView>) => resp.data)
}

export function confirmarConta(idFuncionario: ID) {
  return axios.patch<{ result: UsuarioView }>(`${API_URL}/usuarios/${idFuncionario}/confirma`)
}

/**
 * Atualiza dados do funcionário
 * @param idFuncionario Identificador do funcionário
 * @param funcionario Objeto FuncionarioDtoUpdate
 * @param reprocessarFaturas Recalcula o valor do profissional nos itens das faturas ainda Geradas
 *   (LO-1373). Só vai na query quando verdadeiro: ausente, o backend assume `false`. Exige
 *   `Financeiro:write` além de `Funcionario:update`.
 * @returns FuncionarioView
 */
export const updateFuncionario = (
  idFuncionario: number,
  funcionario: FuncionarioDtoUpdate,
  reprocessarFaturas = false
): Promise<FuncionarioView> => {
  return axios
    .put(`${FUNCIONARIO_URL}/${idFuncionario}`, funcionario, {
      params: reprocessarFaturas ? { reprocessarFaturas: true } : undefined,
    })
    .then((response: AxiosResponse<FuncionarioView>) => response.data)
}

/**
 * Cria um endereço para o funcionário
 * @param idFuncionario Identificador do funcionário
 * @param endereco Objeto EnderecoDtoCreate
 * @returns EnderecoView
 */
export const createEnderecoFuncionario = (
  idFuncionario: ID,
  endereco: EnderecoDtoCreate
): Promise<EnderecoView> => {
  return axios.post(`${FUNCIONARIO_URL}/endereco/${idFuncionario}`, endereco)
}

/**
 * Atualiza o endereço de um funcionário
 * @param idFuncionario Identificador do funcionário
 * @param endereco Objeto EnderecoDtoUpdate
 * @returns EnderecoView
 */
export const updateEnderecoFuncionario = (
  idFuncionario: ID,
  endereco: EnderecoDtoUpdate
): Promise<EnderecoView> => {
  return axios.put(`${FUNCIONARIO_URL}/endereco/${idFuncionario}`, endereco)
}

/**
 *  Busca os endereco de um funcionário
 * @param idFuncionario Identificador do funcionario
 * @returns EnderecoList[]
 */
export const getEnderecoByFuncionario = (idFuncionario: ID) => {
  return axios
    .get(`${FUNCIONARIO_URL}/endereco/${idFuncionario}`)
    .then((response: AxiosResponse<EnderecoList[]>) => response.data)
}

export const deleteFuncionario = (FuncionarioId: ID): Promise<void> => {
  return axios.delete(`${FUNCIONARIO_URL}/${FuncionarioId}`)
}

/**
 * Toggle status ativo/inativo funcionario
 * @param funcionarioId Identificador de funcionario
 * @param ativo status - boolean
 * @returns
 */
export const toggleStatusFuncionario = (funcionarioId: number, ativo: boolean): Promise<void> => {
  return axios.put(`${FUNCIONARIO_URL}/${funcionarioId}`, {
    ativo,
  })
}

export const deleteSelectedFuncionario = (FuncionarioIds: Array<ID>): Promise<void> => {
  const requests = FuncionarioIds.map((id) => axios.delete(`${FUNCIONARIO_URL}/${id}`))
  return axios.all(requests).then(() => {})
}

/**
 * Lista especialidades do profissional logado
 * @returns FuncionarioDtoEspecialidades
 */
export const getEspecialidadesByFuncionario = (): Promise<FuncionarioDtoEspecialidades> => {
  return axios
    .get(`${FUNCIONARIO_URL}/especialidades`)
    .then((response: AxiosResponse<FuncionarioDtoEspecialidades>) => response.data)
}

/**
 * Atualiza dados adicionais de funcionário
 * @param funcionarioId Identificador do funcionario
 * @param data FuncionarioDtoDadosAdicionais
 * @returns FuncionarioView
 */
export const updateDadosAdicionaisFuncionarios = (
  funcionarioId: number,
  data: FuncionarioDtoDadosAdicionais
): Promise<FuncionarioView> => {
  return axios
    .put(`${FUNCIONARIO_URL}/dados/${funcionarioId}`, data)
    .then((response: AxiosResponse<FuncionarioView>) => response.data)
}

/**
 * Busca catálogo CBO por termo de busca
 * @param search Termo de busca
 * @returns CboView[]
 */
export const getCbos = (search?: string): Promise<CboView[]> => {
  return axios.get(`${API_URL}/cbo`, { params: { search } }).then((res: AxiosResponse<any>) => {
    const data = res.data
    if (Array.isArray(data)) return data
    if (Array.isArray(data?.data)) return data.data
    if (Array.isArray(data?.content)) return data.content
    return []
  })
}

/**
 * Busca lotações do funcionário por ID
 * @param funcionarioId Identificador do funcionário
 * @returns LotacaoView[]
 */
export const getLotacoesByFuncionario = (funcionarioId: number): Promise<LotacaoView[]> => {
  return axios
    .get(`${API_URL}/funcionarios/${funcionarioId}/lotacoes`)
    .then((res: AxiosResponse<LotacaoView[]>) => res.data)
}

/**
 * Cria nova lotação para o funcionário
 * @param funcionarioId Identificador do funcionário
 * @param data Dados para criação da lotação
 * @returns LotacaoView
 */
export const createLotacao = (
  funcionarioId: number,
  data: LotacaoDtoCreate
): Promise<LotacaoView> => {
  return axios
    .post(`${API_URL}/lotacoes/${funcionarioId}`, data)
    .then((res: AxiosResponse<LotacaoView>) => res.data)
}

/**
 * Atualiza lotação existente
 * @param id Identificador da lotação
 * @param data Dados de atualização
 * @returns LotacaoView
 */
export const updateLotacao = (id: number, data: LotacaoDtoUpdate): Promise<LotacaoView> => {
  return axios
    .put(`${API_URL}/lotacoes/${id}`, data)
    .then((res: AxiosResponse<LotacaoView>) => res.data)
}

/**
 * Inativa uma lotação
 * @param id Identificador da lotação
 * @returns
 */
export const deleteLotacao = (id: number): Promise<void> => {
  return axios.patch(`${API_URL}/lotacoes/${id}/inativar`).then(() => {})
}

/**
 * Reativa uma lotação inativa
 * @param id Identificador da lotação
 * @returns
 */
export const reactivateLotacao = (id: number): Promise<void> => {
  return axios.patch(`${API_URL}/lotacoes/${id}/reativar`).then(() => {})
}

/** Lista de documentos da pessoa (SBIS ECF.02.01): `GET/POST/PUT/DELETE /funcionarios/documento`. */
export const documentosFuncionarioRequests = criarRequestsDocumentos(FUNCIONARIO_URL)
